import pRetry from "p-retry";
import { getTrackingCard } from "./tracking";
import { buildUserIdPayload } from '../utils/userIdPayload';

/**
 * Serialize a date using the calendar date and clock time selected by the user.
 *
 * The API expects ISO strings, but courier dates/times are local values rather
 * than instants. Building the ISO value as UTC keeps those local components
 * unchanged regardless of the browser timezone.
 */
export function serializeLocalDate(value) {
    if (!(value instanceof Date)) {
        return value;
    }

    return new Date(Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
        value.getMilliseconds()
    )).toISOString();
}

export async function getCities({ query, signal }) {
    return await pRetry(
      async () => {
        return await _getCities({ query, signal });
      },
      { retries: 5 }
    );
  }
  
export async function _getCities({ query, signal }) {
    const response = await window.fetch(`https://www.cdek.ru/api-lkfl/cities/autocomplete/?` + new URLSearchParams({ str: query, limit: 10 }), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X)',
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7'
      },
      signal, // Pass the AbortSignal here
    });
  
    if (!response.ok) {
      return [];
    }
  
    const data = await response.json();

    // filter by country code
    const allowed_codes = [1, 15, 41, 42, 48];
    let result = data?.data.filter(city => allowed_codes.includes(city.countryCode));
    let _data = result.map(({ id, fullName, countryCode }) => ({ value: id, label: fullName, countryCode: countryCode }));

    return _data;
}

export async function getCitiesConst({contract_number}) {
    console.log("hello")
    return await pRetry(
      async () => {
        return await _getCitiesConst({contract_number});
      },
      { retries: 5 }
    );
  }
  
export async function _getCitiesConst({contract_number}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_pvz_const`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contract_number
      })
    });
  
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
  
    const data = await response.json();
    console.log(data)
    let result = data?.result.map(({ name, value }) => ({ value: value, label: name }));

    return result;
}

export async function getPopularCities() {
    return await pRetry(
      async () => {
        return await _getPopularCities();
      },
      { retries: 5 }
    );
  }
  
export async function _getPopularCities() {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_popular_cities`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
  
    const COUNTRY_CODE_MAP = {
        RU: 1,
        BY: 42,
        KZ: 48,
        KG: 15,
        AM: 41
    };
  
    const data = await response.json();
    let result = data?.result.map(({ id, name, country_code }) => ({ 
        value: id, 
        label: name,
        countryCode: COUNTRY_CODE_MAP[country_code] ?? 1
    }));

    return result;
}

export async function getTariffs({contract_number, lang, city_from, city_to, packages, contract_vat_percent}) {
    // Check if calculated weight ( (height * width * length) / 5000) is greater than total weight
    // and if it is, then replaec places weight with his calculcated weight

    console.log('packages', packages);

    packages = packages.map(packageItem => {
        let calculated_weight = Math.ceil((packageItem.height * packageItem.width * packageItem.length) / 5000 * 100) / 100;

        return {
            ...packageItem,
            weight: calculated_weight > packageItem.weight ? calculated_weight : packageItem.weight,
            goods: packageItem.goods.map(good => {
                return {
                    ...good,
                    vat_rate: contract_vat_percent,
                }
            })
        }
    });

    // Map language to supported values (kz -> ru, since kz is not supported by tariff API)
    const supportedLang = lang === 'kz' ? 'ru' : lang;


    return await pRetry(async () => {
        return await _getTariffs({contract_number, lang: supportedLang, city_from, city_to, packages});
    }, {retries: 5});
}

export async function _getTariffs({contract_number, lang, city_from, city_to, packages}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_tariffs`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number: contract_number,
            lang: lang,
            from_location: {code: city_from},
            to_location: {code: city_to},
            packages: packages
        })
    });

    return (await response.json())?.result;
}

export async function getAdditions({invoiceForm}) {
    return await pRetry(async () => {
        return await _getAdditions({invoiceForm});
    }, {retries: 5});
}

export async function _getAdditions({invoiceForm}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_additions`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            ...invoiceForm
        })
    });

    return (await response.json())?.result;
}

export async function getDeliveryTime({date}) {
    return await pRetry(async () => {
        return await _getDeliveryTime({date});
    }, {retries: 5});
}

export async function _getDeliveryTime({date}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_delivery_time`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            date
        })
    });

    return (await response.json())?.result;
}

export async function getDeliveryTimeCouirer({contract_number, uuid, date}) {
    return await pRetry(async () => {
        return await _getDeliveryTimeCouirer({contract_number, uuid, date});
    }, {retries: 5});
}

export async function _getDeliveryTimeCouirer({contract_number, uuid, date}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_delivery_time_couirer`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            uuid,
            date
        })
    });

    return (await response.json())?.result;
}

export async function getDeliveryDateCouirer({contract_number, uuid, date}) {
    return await pRetry(async () => {
        return await _getDeliveryDateCouirer({contract_number, uuid, date});
    }, {retries: 5});
}

export async function _getDeliveryDateCouirer({contract_number, uuid, date}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_delivery_date_couirer`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            uuid
        })
    });

    return (await response.json())?.result;
}

export async function getDeliveryDates({contract_number, from_location_code, day_to}) {
    return await pRetry(async () => {
        return await _getDeliveryDate({contract_number, from_location_code, day_to});
    }, {retries: 5});
}

export async function _getDeliveryDate({contract_number, from_location_code, day_to}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_available_delivery_dates`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            from_location_code,
            day_to
        })
    });

    return (await response.json())?.result;
}

export async function getDeliveryTimeCdek({date, uuid, contract_number}) {
    return await pRetry(async () => {
        return await _getDeliveryTimeCdek({date, uuid, contract_number});
    }, {retries: 5});
}

export async function _getDeliveryTimeCdek({date, uuid, contract_number}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_delivery_time`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            date,
            uuid,
            contract_number
        })
    });

    return (await response.json())?.result;
}

export async function getPackageInfo(body) {
    return _getPackageInfo(body);
}

export async function _getPackageInfo(body) {
    return getTrackingCard(body);
}

export async function getPVZ({contract_number, city_code, weight, pay, trying, is_ltl}) {
    return await pRetry(async () => {
        return await _getPVZ({contract_number, city_code, weight, pay, trying, is_ltl});
    }, {retries: 5});
}

export async function _getPVZ({contract_number, city_code, weight, pay, trying, is_ltl}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_pvz`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            city_code,
            weight,
            pay,
            trying,
            is_ltl
        })
    });

    return (await response.json())?.result;
}

// Создание накладной не идемпотентно: повтор после сетевого сбоя регистрирует в СДЭК
// вторую накладную, поэтому одна попытка (REFACTORING-48).
export async function createInvoice({contract_number, platform, userId, params}) {
    return await _createInvoice({contract_number, platform, userId, params});
}

export async function _createInvoice({contract_number, platform, userId, params}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/create_invoice`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            ...buildUserIdPayload(platform, userId),
            params
        })
    });

    const body = await response.json();
    if (!response.ok) {
        return { error: body?.error || 'invoice_creation_failed', details: body?.details };
    }
    return body?.result;
}

export async function createTemplate({contract_number, params}) {
    return await pRetry(async () => {
        return await _createTemplate({contract_number, params});
    }, {retries: 5});
}

export async function _createTemplate({contract_number, params}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/template/create_template`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            params: {
                ...params,
                sender: JSON.stringify(params.sender),
                reciver: JSON.stringify(params.reciver),
                places: JSON.stringify(params.places),
                services: JSON.stringify(params.services),
            }
        })
    });

    return (await response.json())?.result;
}

export async function updateTemplate({template_id, params}) {
    return await pRetry(async () => {
        return await _updateTemplate({template_id, params});
    }, {retries: 5});
}

export async function _updateTemplate({template_id, params}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/template/update_template`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            template_id,
            params: {
                ...params,
                sender: JSON.stringify(params.sender),
                reciver: JSON.stringify(params.reciver),
                places: JSON.stringify(params.places),
                services: JSON.stringify(params.services),
            }
        })
    });

    return (await response.json())?.result;
}


export async function deletetrackNumber({track_number}) {
    return await pRetry(async () => {
        return await _deletetrackNumber({track_number});
    }, {retries: 5});
}

export async function _deletetrackNumber({track_number}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/tracking/delete_track_number`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            track_number: track_number
        })
    });

    return (await response.json())?.status;
}


export async function callback({platform, userId, access_token, contract_number, uuid, couirer, session}) {
    return await pRetry(async () => {
        return await _callback({platform, userId, access_token, contract_number, uuid, couirer, session});
    }, {retries: 5});
}

export async function _callback({platform, userId, access_token, contract_number, uuid, couirer, session}) {

    if (couirer) {
        couirer = Object.fromEntries(
            Object.entries(couirer).map(([key, value]) => [key, serializeLocalDate(value)])
        );
    }

    const userIdPayload = buildUserIdPayload(platform, userId);
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/callback`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(couirer ? {
            ...userIdPayload,
            access_token,
            contract_number,
            uuid,
            courier: couirer,
            session
        } : {
            ...userIdPayload,
            access_token,
            contract_number,
            uuid,
            session
        })
    });
}

export async function callback_template({platform, userId, contract_number, template_name, session}) {
    return await pRetry(async () => {
        return await _callback_template({platform, userId, contract_number, template_name, session});
    }, {retries: 5});
}

export async function _callback_template({platform, userId, contract_number, template_name, session}) {

    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/template/callback`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            ...buildUserIdPayload(platform, userId),
            contract_number,
            template_name,
            session
        })
    });
}

export async function callback_couirer({contract_number, uuid,
    courier_date, courier_time_from, courier_time_to,
    courier_break_from, courier_break_to, session}) {
    return await pRetry(async () => {
        return await _callback_couirer({contract_number, uuid,
            courier_date, courier_time_from, courier_time_to,
            courier_break_from, courier_break_to, session});
    }, {retries: 5});
}

export async function _callback_couirer({contract_number, uuid,
    courier_date, courier_time_from, courier_time_to,
    courier_break_from, courier_break_to, session}) {

    courier_date = serializeLocalDate(courier_date);
    courier_time_from = serializeLocalDate(courier_time_from);
    courier_time_to = serializeLocalDate(courier_time_to);
    courier_break_from = serializeLocalDate(courier_break_from);
    courier_break_to = serializeLocalDate(courier_break_to);
    
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/callback_couirer`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            uuid,
            courier_date,
            courier_time_from,
            courier_time_to,
            courier_break_from,
            courier_break_to,
            session
        })
    });
}

export async function calculateInvoice({ platform, userId }, form, session) {
    return await pRetry(async () => {
        return await _calculateInvoice({ platform, userId }, form, session);
    }, {retries: 5});
}

export async function _calculateInvoice({ platform, userId }, form, session) {

    const services = { ...form.services };
    if ('BOX' in services) {
        delete services.BOX;
        console.log("box deleted");
    }

    console.log(form);

    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/calculate/invoice`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            ...buildUserIdPayload(platform, userId),
            from_location: form.city_label_from,
            to_location: form.city_label_to,
            packages: form.places,
            delivery_sum: form.delivery_sum,
            contract_number: form.contract_number,
            tariff_name: form.tariff_name,
            period_max: form.period_max,
            period_min: form.period_min,
            services: form.services,
            session
        })
    });

    if (!response.ok) {
        const errorDetails = await response.json();
        console.error('Ошибка запроса:', response.status, errorDetails);
        throw new Error('Ошибка запроса: ' + response.status);
    }

    return await response.json();
}

export async function getSuggestedCities({contract_number, query}) {
    return await pRetry(async () => {
        return await _getSuggestedCities({contract_number, query});
    }, {retries: 5});
}

export async function _getSuggestedCities({contract_number, query}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_suggested_cities`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            query
        })
    });

    if (!response.ok) {
        // Fail silently and return empty array to avoid breaking user flow
        return [];
    }

    const data = await response.json();

    const COUNTRY_CODE_MAP = {
        RU: 1,
        BY: 42,
        KZ: 48,
        KG: 15,
        AM: 41
    };

    const suggestionsRaw = data?.result || [];

    const suggestions = suggestionsRaw.map((item) => ({
        label: item.full_name || item.label || `${item.country || ''}, ${item.city || ''}`,
        value: item.code,
        countryCode: COUNTRY_CODE_MAP[item.country_code] ?? 1
    }));

    return suggestions;
}

export async function getCityInfo({contract_number, code}) {
    return await pRetry(async () => {
        return await _getCityInfo({contract_number, code});
    }, {retries: 5});
}

export async function _getCityInfo({contract_number, code}) {
    const response = await window.fetch(`${process.env.REACT_APP_API_URL}/api/invoice/get_city_info`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            contract_number,
            code
        })
    });

    if (!response.ok) {
        // Fail silently but keep interface stable.
        return null;
    }

    const data = await response.json();

    const city = Array.isArray(data?.result) ? data.result[0] : data?.result;
    if (!city) return null;

    // Convert country alpha codes to numeric IDs used previously
    const COUNTRY_CODE_MAP = {
        RU: 1,
        BY: 42,
        KZ: 48,
        KG: 15,
        AM: 41
    };

    return {
        ...city,
        label: city.full_name || `${city.country || ''}, ${city.city || ''}`,
        value: city.code,
        countryCode: COUNTRY_CODE_MAP[city.country_code] ?? 1
    };
}
